import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { FieldErrors } from 'react-hook-form';
import type { PersonalInfoErrors } from 'interfaces';

const TAG_FORM_NAME = 'form.name';
const TAG_FORM_ERROR_TYPE = 'form.error_type';
const TAG_API_STATUS_CODE = 'api.status_code';
const UNKNOWN_STATUS_CODE = 'unknown';

interface ApiErrorLike {
  status_code?: string;
  message?: string;
  error_code?: string;
  data?: unknown;
}

/** Field errors from `statusCodeHelperResponse` (`error.data`) or from HTTP 4xx (`response.data.violations`). */
export const getApiFieldErrors = (error: unknown): PersonalInfoErrors[] => {
  if (axios.isAxiosError(error)) {
    const violations = (error.response?.data as { violations?: unknown } | undefined)?.violations;
    return Array.isArray(violations) ? violations : [];
  }

  const data = (error as ApiErrorLike | null)?.data;
  return Array.isArray(data) ? data : [];
};

export const captureFormValidationErrors = (formName: string, errors: FieldErrors): void => {
  const fields = Object.keys(errors).sort();

  if (!fields.length) {
    return;
  }

  Sentry.captureMessage(`Form validation failed: ${formName}`, {
    level: 'warning',
    tags: {
      [TAG_FORM_NAME]: formName,
      [TAG_FORM_ERROR_TYPE]: 'frontend_validation',
    },
    extra: {
      fields: Object.fromEntries(fields.map(field => [field, errors[field]?.message])),
    },
    fingerprint: ['form-validation-error', formName, ...fields],
  });
};

export const captureFormRequestError = (formName: string, error: unknown): void => {
  // HTTP errors are already reported by the axios interceptors
  if (axios.isAxiosError(error) || axios.isCancel(error)) {
    return;
  }

  const tags = {
    [TAG_FORM_NAME]: formName,
    [TAG_FORM_ERROR_TYPE]: 'request',
  };

  if (error instanceof Error) {
    Sentry.captureException(error, { tags });
    return;
  }

  const {
    status_code: statusCode = UNKNOWN_STATUS_CODE,
    message,
    error_code: errorCode,
  } = (error ?? {}) as ApiErrorLike;

  Sentry.captureMessage(`Form request failed: ${formName} [${statusCode}]`, {
    level: 'error',
    tags: { ...tags, [TAG_API_STATUS_CODE]: statusCode },
    extra: {
      message,
      errorCode,
      fieldErrors: getApiFieldErrors(error).map(({ path, message: fieldMessage, code }) => ({
        path,
        message: fieldMessage,
        code,
      })),
    },
    fingerprint: ['form-request-error', formName, statusCode],
  });
};
